import { Component } from '@theme/component';

export const FAVOURITES_KEY = 'hb_favourites_v1';
export const VISIT_KEY = 'hb_favourites_visit_v2';
export const REMINDER_KEY = 'hb_favourites_dismissed_until_v2';
const IDLE = 1800000;

export function normalizeFavourites(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value.filter(item => {
    if (!item || typeof item.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(item.handle) || seen.has(item.handle)) return false;
    seen.add(item.handle);
    return true;
  }).slice(0, 100).map(item => ({ handle: item.handle, savedAt: Number.isFinite(item.savedAt) ? item.savedAt : 0 }));
}

export function toggleFavourite(items, handle, now) {
  const current = normalizeFavourites(items);
  if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return current;
  if (current.some(item => item.handle === handle)) return current.filter(item => item.handle !== handle);
  if (current.length >= 100) return current;
  return [{ handle, savedAt: now }, ...current];
}

export function isReturnVisit(visit, now, navigation, external) {
  return !visit || !Number.isFinite(visit.lastSeen) || now - visit.lastSeen > IDLE || (visit.departed && navigation !== 'reload') || (navigation === 'navigate' && external);
}

export function isReturnFromBackground(hiddenAt, now) {
  return Number.isFinite(hiddenAt) && now - hiddenAt >= 5000;
}

export function reminderAllowed({ returning, count, shown, nextAt, now, pathname }) {
  return returning && count > 0 && !shown && now >= nextAt && !/\/(cart|checkout|checkouts)(\/|$)/.test(pathname);
}

class HBFavourites extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort = new AbortController();
    this.words = JSON.parse(this.querySelector('[data-hb-favourite-words]').textContent);
    this.root = this.dataset.root;
    this.persistent = true;
    this.items = this.readStoredFavourites();
    const now = Date.now();
    const priorVisit = this.read('sessionStorage', VISIT_KEY, null);
    const navigation = performance.getEntriesByType('navigation')[0]?.type;
    let external = !document.referrer;
    try { external = !document.referrer || new URL(document.referrer).origin !== location.origin; } catch {}
    const newVisit = isReturnVisit(priorVisit, now, navigation, external);
    this.visit = newVisit ? { lastSeen: now, shown: false, eligible: this.items.length > 0 } : priorVisit;
    this.returning = !!this.visit.eligible;
    this.nextAt = Number(this.read('localStorage', REMINDER_KEY, 0)) || 0;
    this.write('sessionStorage', VISIT_KEY, this.visit);
    this.syncButtons();
    const options = { signal: this.abort.signal };
    document.addEventListener('click', event => this.handleClick(event), { ...options, capture: true });
    window.addEventListener('storage', event => {
      if (event.key === FAVOURITES_KEY || event.key === null) {
        this.items = this.readStoredFavourites();
        this.syncButtons();
        if (this.reminderImageHandle && !this.items.some(item => item.handle === this.reminderImageHandle)) this.prepareReminderImage();
        this.renderPage();
        if (!this.items.length) this.hideReminder();
      }
      if (event.key === REMINDER_KEY) {
        this.nextAt = Number(this.read('localStorage', REMINDER_KEY, 0)) || 0;
        this.hideReminder();
      }
    }, options);
    this.observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-hb-favourite], [data-hb-favourite-count]') || node.querySelector?.('[data-hb-favourite], [data-hb-favourite-count]'))))) this.syncButtons();
    });
    this.observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    this.renderPage();
    this.prepareReminderImage();
    this.scheduleReminder();
    this.heartbeat = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.visit.lastSeen = Date.now();
        this.write('sessionStorage', VISIT_KEY, this.visit);
      }
    }, 15000);
    window.addEventListener('pagehide', () => {
      this.visit.lastSeen = Date.now();
      this.visit.departed = !this.internalNavigation;
      this.write('sessionStorage', VISIT_KEY, this.visit);
    }, options);
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      this.items = this.readStoredFavourites();
      if (this.visit.departed || Date.now() - this.visit.lastSeen > IDLE) this.beginReturn();
      this.syncButtons();
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        this.hiddenAt = Date.now();
        this.visit.hiddenAt = this.hiddenAt;
        this.write('sessionStorage', VISIT_KEY, this.visit);
        this.hideReminder();
        return;
      }
      if (isReturnFromBackground(this.hiddenAt ?? this.visit.hiddenAt, Date.now())) this.beginReturn();
      else if (this.returning && !this.visit.shown) this.scheduleReminder();
      this.hiddenAt = null;
      delete this.visit.hiddenAt;
    }, options);
    document.addEventListener('keydown', event => {
      const reminder = this.querySelector('[data-hb-favourite-reminder]');
      if (event.key === 'Escape' && !reminder.hidden && reminder.contains(document.activeElement)) this.dismissReminder();
    }, options);
  }

  scheduleReminder(delay = 4000) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.maybeRemind(), delay);
  }

  beginReturn() {
    this.items = this.readStoredFavourites();
    this.returning = this.items.length > 0;
    this.visit = { lastSeen: Date.now(), shown: false, eligible: this.returning };
    this.hiddenAt = null;
    this.nextAt = Number(this.read('localStorage', REMINDER_KEY, 0)) || 0;
    this.write('sessionStorage', VISIT_KEY, this.visit);
    this.syncButtons();
    this.prepareReminderImage();
    this.scheduleReminder();
  }

  async prepareReminderImage() {
    const media = this.querySelector('[data-hb-reminder-media]');
    const image = this.querySelector('[data-hb-reminder-image]');
    const title = this.querySelector('[data-hb-reminder-product]');
    if (!media || !image || !title) return;
    const revision = this.reminderImageRevision = (this.reminderImageRevision || 0) + 1;
    if (!this.items.some(item => item.handle === this.reminderImageHandle)) {
      media.hidden = true;
      title.hidden = true;
      this.reminderImageHandle = null;
    }
    if (!this.returning || !this.items.length || /\/(cart|checkout|checkouts)(\/|$)/.test(location.pathname)) return;
    for (const item of this.items.slice(0, 3)) {
      try {
        const response = await fetch(`${this.root}products/${encodeURIComponent(item.handle)}.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal });
        if (!response.ok) continue;
        const product = await response.json();
        if (revision !== this.reminderImageRevision || this.abort.signal.aborted) return;
        if (!product.available || product.handle !== item.handle || !product.featured_image || !this.items.some(saved => saved.handle === item.handle)) continue;
        const url = new URL(product.featured_image, location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) continue;
        url.searchParams.set('width', '192');
        image.src = url.href;
        image.sizes = '(max-width: 749px) 72px, 86px';
        image.srcset = [96, 192].map(width => {
          const source = new URL(url);
          source.searchParams.set('width', String(width));
          return `${source.href} ${width}w`;
        }).join(', ');
        image.onerror = () => {
          if (this.reminderImageHandle === item.handle) { media.hidden = true; title.hidden = true; }
        };
        this.reminderImageHandle = item.handle;
        title.textContent = product.title || '';
        title.hidden = !product.title;
        media.hidden = false;
        return;
      } catch { if (this.abort.signal.aborted) return; }
    }
  }

  dismissReminder() {
    this.hideReminder();
    this.nextAt = Date.now() + IDLE;
    this.write('localStorage', REMINDER_KEY, this.nextAt);
  }

  read(storage, key, fallback) {
    try { return JSON.parse(window[storage].getItem(key) || 'null') ?? fallback; }
    catch { return fallback; }
  }

  readStoredFavourites() {
    try { return normalizeFavourites(JSON.parse(window.localStorage.getItem(FAVOURITES_KEY) || '[]')); }
    catch { return this.items || []; }
  }

  write(storage, key, value) {
    try { window[storage].setItem(key, JSON.stringify(value)); return true; }
    catch { return false; }
  }

  syncButtons() {
    for (const button of document.querySelectorAll('[data-hb-favourite]')) {
      const saved = this.items.some(item => item.handle === button.dataset.hbFavourite);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(saved));
      const label = saved ? this.words.remove : this.words.save;
      button.setAttribute('aria-label', `${label} — ${button.dataset.productTitle || ''}`);
      const text = button.querySelector('[data-hb-favourite-label]');
      if (text) text.textContent = label;
    }
    for (const count of document.querySelectorAll('[data-hb-favourite-count]')) count.textContent = this.items.length ? `(${this.items.length})` : '';
    document.documentElement.toggleAttribute('data-hb-has-favourites', this.items.length > 0);
  }

  handleClick(event) {
    const link = event.target.closest?.('a[href]');
    this.internalNavigation = !!link && !link.target && !event.ctrlKey && !event.metaKey && new URL(link.href, location.href).origin === location.origin;
    const button = event.target.closest?.('[data-hb-favourite]');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      const handle = button.dataset.hbFavourite;
      const saved = this.items.some(item => item.handle === handle);
      if (!saved && this.items.length >= 100) { this.announce(this.words.limit); return; }
      this.items = toggleFavourite(this.items, handle, Date.now());
      this.persistent = this.write('localStorage', FAVOURITES_KEY, this.items);
      this.announce(`${saved ? this.words.removed : this.words.saved}${this.persistent ? '' : ` ${this.words.temporary}`}`);
      this.syncButtons();
      if (this.reminderImageHandle && !this.items.some(item => item.handle === this.reminderImageHandle)) this.prepareReminderImage();
      if (!this.items.length) this.hideReminder();
      if (this.page) this.renderPage(handle);
    }
    if (event.target.closest?.('[data-hb-favourite-dismiss]')) {
      this.dismissReminder();
    }
    if (event.target.closest?.('[data-hb-favourite-retry]')) this.renderPage();
  }

  announce(message) {
    const status = this.querySelector('[data-hb-favourite-status]');
    status.textContent = message;
  }

  async renderPage(focusHandle) {
    this.page = document.querySelector('[data-hb-saved-page]');
    if (!this.page) return;
    const revision = this.renderRevision = (this.renderRevision || 0) + 1;
    const grid = this.page.querySelector('[data-hb-favourite-products]');
    const empty = this.page.querySelector('[data-hb-favourite-empty]');
    const retry = this.page.querySelector('[data-hb-favourite-retry]');
    grid.setAttribute('aria-busy', 'true');
    retry.hidden = true;
    this.productCache ||= new Map();
    const handles = this.items.map(item => item.handle);
    let currency = this.dataset.currency;
    let cartHandles = new Set();
    try {
      const response = await fetch(`${this.root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal });
      if (response.ok) {
        const cart = await response.json();
        currency = cart.currency;
        cartHandles = new Set(cart.items.map(item => item.handle));
      }
    } catch {}
    const results = [];
    for (let index = 0; index < handles.length; index += 4) {
      results.push(...await Promise.all(handles.slice(index, index + 4).map(async handle => {
        if (this.productCache.has(handle)) return this.productCache.get(handle);
        try {
          const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal });
          if (response.status === 404) return { handle, missing: true };
          if (!response.ok) throw new Error('Product unavailable');
          const product = await response.json();
          if (!product.handle || !product.title || !Array.isArray(product.variants)) throw new Error('Invalid product');
          this.productCache.set(handle, product);
          return product;
        } catch { return { handle, failed: true }; }
      })));
      if (revision !== this.renderRevision || this.abort.signal.aborted) return;
    }
    if (revision !== this.renderRevision || this.abort.signal.aborted) return;
    const active = document.activeElement?.closest?.('[data-hb-favourite]');
    const restoreFocus = focusHandle && active && this.page.contains(active);
    const oldIndex = [...grid.children].findIndex(card => card.querySelector(`[data-hb-favourite="${focusHandle}"]`));
    grid.replaceChildren(...results.map(product => this.productCard(product, currency, cartHandles)));
    grid.setAttribute('aria-busy', 'false');
    empty.hidden = results.length > 0;
    empty.textContent = this.words.empty;
    retry.hidden = !results.some(product => product.failed);
    this.syncButtons();
    if (restoreFocus) {
      const next = grid.querySelector(`[data-hb-favourite="${focusHandle}"]`) || grid.children[Math.max(0, Math.min(oldIndex, grid.children.length - 1))]?.querySelector('[data-hb-favourite]');
      if (next) next.focus({ preventScroll: true });
      else {
        const heading = this.page.querySelector('#hb-saved-favourites h2');
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    }
  }

  productCard(product, currency, cartHandles) {
    const card = document.createElement('article');
    card.className = 'hb-saved__card';
    const url = `${this.root}products/${encodeURIComponent(product.handle)}`;
    const link = document.createElement('a');
    link.href = url;
    if (product.featured_image) {
      const image = document.createElement('img');
      const imageUrl = new URL(product.featured_image, location.origin);
      imageUrl.searchParams.set('width', '700');
      image.src = imageUrl.href;
      image.srcset = [300, 500, 700].map(width => {
        const candidate = new URL(imageUrl);
        candidate.searchParams.set('width', String(width));
        return `${candidate.href} ${width}w`;
      }).join(', ');
      image.sizes = '(min-width: 750px) 30vw, 45vw';
      image.alt = product.title;
      image.width = 600;
      image.height = 750;
      image.loading = 'lazy';
      image.className = 'hb-saved__image';
      link.append(image);
    }
    const title = document.createElement('h3');
    title.textContent = product.title || product.handle.replaceAll('-', ' ');
    link.append(title);
    card.append(link);
    const text = document.createElement('p');
    if (product.failed || product.missing) text.textContent = product.failed ? this.words.error : this.words.unavailable;
    else {
      try { text.textContent = `${product.price_varies ? `${this.words.from} ` : ''}${new Intl.NumberFormat(this.dataset.locale, { style:'currency', minimumFractionDigits:0, maximumFractionDigits:0, useGrouping:false, currency }).format(product.price / 100)}`; }
      catch { text.textContent = ''; }
      if (!product.available) text.append(` · ${this.words.unavailable}`);
      if (cartHandles.has(product.handle)) text.append(` · ${this.words.in_cart}`);
    }
    card.append(text);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hb-fav-button';
    button.dataset.hbFavourite = product.handle;
    button.dataset.productTitle = product.title || title.textContent;
    button.append(this.querySelector('[data-hb-heart-template]').content.cloneNode(true));
    card.append(button);
    if (!product.missing) {
      const choose = document.createElement('a');
      choose.className = 'hb-button hb-button--outline';
      choose.href = url;
      choose.textContent = this.words.choose;
      card.append(choose);
    }
    return card;
  }

  maybeRemind() {
    if (!reminderAllowed({ returning: this.returning, count: this.items.length, shown: this.visit.shown, nextAt: this.nextAt, now: Date.now(), pathname: location.pathname })) return;
    const typing = document.activeElement?.matches('input,textarea,select,[contenteditable="true"]');
    const overlay = [...document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"]:not([aria-hidden="true"]):not([hidden])')].some(element => {
      const bounds = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return style.visibility !== 'hidden' && Number(style.opacity) > 0 && bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < innerHeight && bounds.right > 0 && bounds.left < innerWidth;
    });
    if (document.visibilityState !== 'visible' || typing || overlay) {
      this.scheduleReminder(4000);
      return;
    }
    this.querySelector('[data-hb-favourite-reminder]').hidden = false;
    document.querySelector('hb-cart-reminder')?.setAttribute('hidden', '');
    this.visit.shown = true;
    this.write('sessionStorage', VISIT_KEY, this.visit);
    clearTimeout(this.autoHide);
    this.autoHide = setTimeout(() => {
      if (!this.querySelector('[data-hb-favourite-reminder]').contains(document.activeElement)) this.hideReminder();
    }, 30000);
  }

  hideReminder() {
    this.querySelector('[data-hb-favourite-reminder]').hidden = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.timer);
    clearTimeout(this.autoHide);
    clearInterval(this.heartbeat);
    this.renderRevision = (this.renderRevision || 0) + 1;
    this.reminderImageRevision = (this.reminderImageRevision || 0) + 1;
    const image = this.querySelector('[data-hb-reminder-image]');
    if (image) image.onerror = null;
  }
}

if (!customElements.get('hb-favourites')) customElements.define('hb-favourites', HBFavourites);